import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// ============================================================================
// Riportok — DTO-k a 23-logic.js reportsVals/topProductsVals függvényeiből
// levezetve (12-riportok.html).
// ============================================================================

export type ReportRange = 'day' | 'week' | 'month' | 'year' | 'custom';

export interface ReportFilter {
  range: ReportRange;
  /** 'YYYY-MM-DD' — csak range='custom' esetén */
  from?: string;
  /** 'YYYY-MM-DD' — csak range='custom' esetén */
  to?: string;
  /** webshop név vagy nincs megadva = mind */
  shop?: string;
  /** ISO country code (pl. 'DE') vagy nincs megadva = mind */
  country?: string;
}

export interface ReportSalesPoint {
  /** tengelyfelirat: óra ('8h'), nap ('H', '1'), hónap ('Jan') vagy dátum */
  label: string;
  /** értékesítés EUR */
  sales: number;
  /** profit EUR */
  profit: number;
}

export interface ReportSales {
  /** ember-olvasható időszak felirat, pl. 'ez a hét, naponta' */
  rangeLabel: string;
  points: ReportSalesPoint[];
  totalSales: number;
  totalProfit: number;
  /** rendelésszám az időszakban */
  orderCount: number;
  avgOrderValue: number;
  /** profit / sales × 100, kerekítve */
  marginPct: number;
}

export interface ReportCountryRow {
  /** ISO kód, pl. 'DE' */
  countryCode: string;
  /** megjelenített név, pl. 'Németország (DE)' */
  countryName: string;
  orders: number;
}

export interface ReportTopProductRow {
  productName: string;
  sku: string;
  shop: string;
  countryCode: string;
  quantity: number;
  /** eladási egységár EUR */
  unitPrice: number;
  revenue: number;
  /** webshop díj % és összege EUR */
  feePct: number;
  fee: number;
  /** szállítási költség EUR/db */
  shippingCost: number;
  /** vám % és összege EUR */
  dutyPct: number;
  duty: number;
  /** gyártási önköltség EUR/db */
  productionCost: number;
  unitProfit: number;
  profit: number;
  marginPct: number;
}

export interface ReportShippingCostRow {
  countryCode: string;
  /** átlagos szállítási költség EUR/csomag */
  avgCost: number;
}

@Injectable({ providedIn: 'root' })
export class ReportsService {

  constructor(private http: HttpClient) {}

  private params(filter: ReportFilter): HttpParams {
    let params = new HttpParams().set('range', filter.range);
    if (filter.from) params = params.set('from', filter.from);
    if (filter.to) params = params.set('to', filter.to);
    if (filter.shop) params = params.set('shop', filter.shop);
    if (filter.country) params = params.set('country', filter.country);
    return params;
  }

  /** GET reports/sales?range=&from=&to=&shop=&country= — grafikon + KPI-k */
  getSales(filter: ReportFilter): Observable<ReportSales> {
    return this.http.get<ReportSales>('reports/sales', { params: this.params(filter) });
  }

  /** GET reports/countries?... — top országok rendelésszám szerint */
  getCountries(filter: ReportFilter): Observable<ReportCountryRow[]> {
    return this.http.get<ReportCountryRow[]>('reports/countries', { params: this.params(filter) });
  }

  /** GET reports/top-products?... — legprofitábilisabb termék+ország kombók */
  getTopProducts(filter: ReportFilter): Observable<ReportTopProductRow[]> {
    return this.http.get<ReportTopProductRow[]>('reports/top-products', { params: this.params(filter) });
  }

  /** GET reports/shipping-costs?... — átlagos szállítási költség országonként */
  getShippingCosts(filter: ReportFilter): Observable<ReportShippingCostRow[]> {
    return this.http.get<ReportShippingCostRow[]>('reports/shipping-costs', { params: this.params(filter) });
  }
}
